import axios from 'axios';

/* ============================================================
   canalApi — capa de datos del Canal del Método (cliente).
   Consume los MISMOS endpoints que alimenta el admin (autopista
   única). Read-only (Fase 1). Si no hay sesión/backend, cada
   función devuelve null y el front cae a datos mock.

   Endpoints PÚBLICOS (sin auth): relajaciones, recomendaciones,
   anuncios, botones → funcionan también en la preview sin login.
   Endpoints PROTEGIDOS (token): semana actual, clases → solo con
   sesión admin/cliente; si no, null → mock.
   ============================================================ */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// tipo_slot_compatible (backend) → nivel + modalidad (front)
const SLOT_MAP = {
  estandar_paralelo: { nivel: 'estandar', modalidad: 'Paralelo' },
  estandar_apertura: { nivel: 'estandar', modalidad: 'Apertura' },
  estandar_mixta:    { nivel: 'estandar', modalidad: 'Mixta' },
  suave_paralelo:    { nivel: 'suave', modalidad: 'Paralelo' },
  suave_apertura:    { nivel: 'suave', modalidad: 'Apertura' },
};
// orden de las 5 clases de la semana (suave primero, luego estándar)
const SLOT_ORDER = ['suave_paralelo', 'suave_apertura', 'estandar_paralelo', 'estandar_apertura', 'estandar_mixta'];

const hasToken = () => { try { return !!localStorage.getItem('token'); } catch (e) { return false; } };

async function safeGet(path, { auth = false } = {}) {
  if (auth && !hasToken()) return null; // sin sesión no llamamos a endpoints protegidos
  try {
    // Cache-busting + no-cache: fuerza datos FRESCOS en cada entrada al canal
    // (si no, el navegador puede servir la respuesta cacheada vieja y no se ve
    //  lo recién publicado). Patrón ?t= como el resto del CRM.
    const res = await axios.get(`${API}${path}`, {
      timeout: 8000,
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    });
    return res.data;
  } catch (e) {
    return null; // red caída, 401/403, backend ausente… → fallback mock
  }
}

// ¿Esta persona ya vio la bienvenida/confeti? (por USUARIO, no por navegador).
export async function getBienvenidaVista() {
  const d = await safeGet('/cliente/bienvenida-vista', { auth: true });
  return !!(d && d.vista === true);
}

// ¿Debe AUTO-lanzarse el tutorial del reproductor a ESTA persona? (servidor, por usuario)
export async function getTutorialReproductor() {
  try {
    const res = await axios.get(`${API}/cliente/tutorial-reproductor`, { timeout: 6000 });
    return res.data && res.data.lanzar === true;
  } catch (e) { return false; } // fail-safe: no lanzar
}

// Semana vigente: 5 slots + comentario semanal (notas_generales)
export async function getSemanaActual() {
  // El endpoint devuelve { semana: {...} } (o { semana: null } si no hay vigente).
  const data = await safeGet('/canal-semanas/actual', { auth: true });
  const semana = data && data.semana;
  if (!semana) return null;
  // slots: dict keyed por tipo_slot_compatible; cada slot trae
  // { titulo, descripcion, profesor, tipo, nivel_visible, nota_semanal, video_url, thumbnail_url, thumbnail_time }
  const slots = semana.slots || {};
  // Extras de la semana (contrato Videos-Backend clases_extra). Puede NO venir
  // (semanas antiguas / prod sin desplegar) o venir [] → se comporta como hoy.
  const extrasRaw = Array.isArray(semana.clases_extra) ? semana.clases_extra : [];

  // Un slot o una extra comparten forma; los mapeamos igual. `esExtra` distingue.
  const mapClase = (key, s, opts = {}) => {
    const map = SLOT_MAP[key] || { nivel: '', modalidad: '' };
    return {
      id: opts.id || key,
      tipo: 'clase',
      esExtra: !!opts.esExtra, // extra de la semana (no es uno de los 5 slots fijos)
      slotTipo: key,
      contenidoId: s.clase_id || opts.id || key,
      nivel: map.nivel,
      modalidad: map.modalidad,
      duracion: s.duracion || null,
      nombre: s.titulo || `${map.nivel} · ${map.modalidad}`,
      profesor: s.profesor || '', // quién da la clase (contrato canal_semanas)
      descripcion: s.descripcion || '',
      thumbnail: s.thumbnail_url || null,
      video: s.video_url || null,
      notaSemanal: s.nota_semanal || '',
      comoHacerla: s.como_hacerla || null,
      complementos: s.complementos || [],
      consejoPilar: s.consejo_pilar || null,
      palabrasClave: s.palabras_clave || [],
      esMpd: !!s.es_mpd, // marca MPD (contrato backend canal_semanas): filtra la rutina en "Clases MPD"
      // Flag técnico activo (backend #1176): si el profesor está dado de baja, la clase
      // aún se muestra pero NO se destaca como "Selección de la semana". Ausente → activo.
      tecnicoActivo: s.tecnico_activo !== false,
      hecha: false, // sin capa de eventos todavía (Fase A)
      // No recomendar en el hero una clase de técnico dado de baja (#1176): sigue en el
      // listado, pero no se destaca como recomendada.
      recomendada: !opts.esExtra && key === 'estandar_mixta' && s.tecnico_activo !== false,
    };
  };

  // Orden (criterio Ann): PRIMERO las 5 clases fijas de la semana, seguidas;
  // DESPUÉS todas las extras (no intercaladas por tipo).
  const clases = [];
  SLOT_ORDER.forEach((key) => {
    const s = slots[key];
    if (s) clases.push(mapClase(key, s));
  });
  SLOT_ORDER.forEach((key) => {
    extrasRaw
      .filter((e) => e && e.tipo === key)
      .forEach((e, i) => clases.push(mapClase(key, e, { esExtra: true, id: `${key}:extra:${e.clase_id || i}` })));
  });
  extrasRaw
    .filter((e) => e && !SLOT_ORDER.includes(e.tipo))
    .forEach((e, i) => clases.push(mapClase(e.tipo || 'extra', e, { esExtra: true, id: `extra:${e.clase_id || i}` })));

  if (clases.length === 0) return null;
  return {
    clases,
    comentarioSemanal: semana.notas_generales || '',
    consejoVideo: semana.consejo_video_url || null,
    consejoPersona: semana.consejo_persona || null,
    fechaInicio: semana.fecha_inicio,
    fechaFin: semana.fecha_fin,
  };
}

// Biblioteca de clases (todas, filtrables por nivel/modalidad)
// Su suscripción y sus facturas, de Membership Pro (Ann 17-jul-2026).
// null = NO SE SABE (WordPress caído o sin configurar) ≠ "no tiene nada".
// La pantalla distingue: nunca dice "no tienes facturas" si no lo sabemos.
export async function getMisPagos() {
  // Ann 2026-09-10: el router se monta SIN prefijo, así que «/canal-pagos/…» daba 404 y la
  // pantalla de la socia salía vacía… y sin botón para darse de baja. Comprobado en producción.
  const d = await safeGet('/mis-pagos', { auth: true });
  return (d && d.datos) || null;
}

// ¿Bloqueada por deuda (recibo devuelto)? + mensaje claro con el mes (Ann 2026-08-04).
export async function getMiBloqueoDeuda() {
  const d = await safeGet('/mi-bloqueo-deuda', { auth: true });
  return d || null;
}

export async function getClasesBiblioteca() {
  const data = await safeGet('/canal-clases', { auth: true });
  if (!Array.isArray(data)) return null;
  const out = data
    .filter((c) => (c.estado || 'activo') === 'activo')
    .map((c) => {
      const map = SLOT_MAP[c.tipo_slot_compatible] || {};
      return {
        id: c.id,
        tipo: 'clase',
        contenidoId: c.id,
        nombre: c.titulo_visible || 'Clase',
        nivel: map.nivel === 'estandar' ? 'Estándar' : map.nivel === 'suave' ? 'Suave' : '',
        modalidad: map.modalidad || '',
        duracion: c.duracion || null,
        thumbnail: c.thumbnail_presigned || null,
        video: c.video_presigned_url || null,
        descripcion: c.descripcion || '',
        profesor: c.profesor || '',
        consejoPilar: c.consejo_pilar || null,
        comoHacerla: c.como_hacerla || null,
        complementos: c.complementos || [],
      };
    });
  return out.length ? out : null;
}

// Campos de la ficha comunes (categorías, descripción, consejo de Pilar) → item.
function _camposFicha(r) {
  const cats = (r.categorias || '').split(',').map(s => s.trim()).filter(Boolean);
  const cp = r.consejo_pilar || {};
  const consejoPilar = (cp.texto || cp.embed_url || cp.video_url)
    ? { texto: cp.texto || '', embedUrl: cp.embed_url || null, video: cp.video_url || null }
    : null;
  return {
    categorias: cats,
    descripcion: r.descripcion || '',
    consejoPilar,
  };
}

// Relajaciones y meditaciones activas (endpoint público)
export async function getRelajaciones() {
  const data = await safeGet('/canal-relajaciones/wp/activas', { auth: true });
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map((r, i) => ({
    id: r.id || `rel-${i}`,
    tipo: 'relajacion',
    contenidoId: r.id || `rel-${i}`,
    nombre: r.titulo || 'Relajación',
    zona: r.subtitulo || 'Relajación',
    duracion: r.duracion || null,
    thumbnail: r.thumbnail_url || null,
    video: r.video_url || null,
    embedUrl: r.embed_url || null,
    grad: 'gmed',
    ..._camposFicha(r),
  }));
}

// Extras "vida diaria" = recomendaciones ergonómicas activas (público)
export async function getRecomendaciones() {
  const data = await safeGet('/canal-recomendaciones/wp/activas', { auth: true });
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map((r, i) => ({
    id: r.id || `rec-${i}`,
    tipo: 'recomendacion',
    contenidoId: r.id || `rec-${i}`,
    nombre: r.titulo || 'Recomendación',
    zona: 'Vida diaria',
    duracion: r.duracion || null,
    thumbnail: r.thumbnail_url || null,
    video: r.video_url || null,
    embedUrl: r.embed_url || null,
    grad: 'g1',
    ..._camposFicha(r),
  }));
}

// Miniatura de YouTube derivada del enlace embed (cuando no hay thumbnail guardado).
function ytThumbFromUrl(url = '') {
  const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

// Charlas / entrevistas "Conoce el método" (público). Vídeo S3 protegido o embed YouTube/Vimeo.
export async function getCharlas() {
  // /public pasó a exigir auth + acceso al Canal (Guardián): mandar token.
  const data = await safeGet('/canal-charlas/public', { auth: true });
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map((c, i) => ({
    id: c.id || `charla-${i}`,
    tipo: 'charla',
    contenidoId: c.id || `charla-${i}`,
    nombre: c.titulo || 'Charla',
    tipoEtiqueta: c.tipo || 'Charla',
    persona: c.persona || '',
    duracion: c.duracion || null,
    descripcion: c.descripcion || '',
    thumbnail: c.thumbnail_url || ytThumbFromUrl(c.embed_url) || null,
    video: c.video_url || null,        // S3 presigned (protegido)
    embedUrl: c.embed_url || null,     // YouTube/Vimeo (abierto)
    fuente: c.fuente || '',
    protegido: !!c.protegido,
    grad: 'gclase',
  }));
}

// Banner del tablón (público)
export async function getAnuncio() {
  const data = await safeGet('/canal-anuncios/wp/activo');
  if (!data || !data.activo) return null;
  return data;
}

// Rotativo de portada (público): anuncios/novedades marcados «Destacar en portada».
// Contrato Videos-Backend: GET /canal-anuncios/wp/rotativo → items ya filtrados por
// fechas/orden. Mapeo a la forma de slide del HeroRotativo (texto ← subtitulo).
export async function getRotativoPortada() {
  const data = await safeGet('/canal-anuncios/wp/rotativo?ambito=cliente&limite=6');
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map(d => ({
    id: d.id,
    tipo: d.patrocinado ? 'anuncio' : 'novedad',
    imagen_url: d.imagen_fondo_url || '',
    kicker: d.kicker || '',
    etiqueta: '',
    titulo: d.titulo || '',
    texto: d.subtitulo || '',
    cta_texto: d.cta_texto || '',
    cta_url: d.cta_url || '',
    accion: d.accion || '', // botón especial: 'reserva_fisio' | 'ir:<seccion>'
    patrocinado: !!d.patrocinado,
    segundos: Number(d.segundos) > 0 ? Number(d.segundos) : 0, // duración de la diapo (0 = automático)
    secciones: Array.isArray(d.secciones) ? d.secciones : [],
    color_texto: d.color_texto || '',
    pos_pc: d.imagen_fondo_pos_pc || 'center',
    pos_tablet: d.imagen_fondo_pos_tablet || 'center',
    pos_movil: d.imagen_fondo_pos_movil || 'center',
  }));
}

// Agregador del menú "Clases MPD": TODO el contenido marcado es_mpd (clases + relajaciones +
// consejos + fichas de área) en una lista. Contrato backend GET /canal-clases/mpd/agregado.
export async function getClasesMpdAgregado() {
  const data = await safeGet('/canal-clases/mpd/agregado', { auth: true });
  if (!Array.isArray(data)) return [];
  return data.map(d => {
    // Misma nomenclatura que el dashboard: nivel (coloreado) + modalidad derivados de
    // tipo_slot_compatible. Solo las clases lo traen; el resto cae al título libre.
    const map = SLOT_MAP[d.tipo_slot_compatible] || {};
    return {
      id: d.id,
      tipo: d.tipo,
      contenidoId: d.id,
      nombre: d.titulo || '',
      descripcion: d.subtitulo || '',
      thumbnail: d.thumbnail_url || null,
      bloqueada: !!d.bloqueada,
      esMpd: true,
      areaKey: d.area_key || '',
      nivel: map.nivel || '',
      modalidad: map.modalidad || '',
      profesor: d.profesor || '',
    };
  });
}

// Membresía REAL del cliente (protegido). Fuente única: /client/mi-perfil → canal_online.membresia
// (resuelta desde membresia_id → config_membresias; NUNCA hardcodear "Premium", fue un bug).
export async function getMiMembresia() {
  const data = await safeGet('/client/mi-perfil', { auth: true });
  const m = data?.canal_online?.membresia || null;
  if (!m || m === 'Sin membresía') return null;
  return m;
}

// Email del cliente logueado (para detectar cuentas de demo de la bienvenida).
export async function getMiEmail() {
  const data = await safeGet('/client/mi-perfil', { auth: true });
  return (data?.personal?.email || '').trim();
}

// Botones del canal (público)
export async function getBotones() {
  const data = await safeGet('/canal-botones/public');
  if (!Array.isArray(data) || data.length === 0) return null;
  return data;
}

// Novedades del canal (público): tarjetas "Lo último del canal"
export async function getNovedades() {
  // FUENTE ÚNICA: el mensaje/novedad al cliente del editor de Controles
  // (/client/anuncio-cliente). Mismo texto que la marquesina de la landing.
  // Sin mensaje activo → null (la pantalla muestra su vacío, sin demo).
  const a = await safeGet('/client/anuncio-cliente', { auth: true });
  if (!a || !a.activo || !a.texto) return null;
  return [{ id: 'anuncio-cliente', titulo: a.texto, texto: '', enlace: '', imagen_url: null }];
}

// ── FAVORITOS (por usuario logueado) ──
export async function getFavoritos() {
  if (!hasToken()) return [];
  try {
    const res = await axios.get(`${API}/canal-favoritos/`, { timeout: 8000 });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { return []; }
}
export async function addFavorito({ tipo, contenidoId, titulo }) {
  try {
    await axios.post(`${API}/canal-favoritos/`, { tipo, contenido_id: contenidoId, titulo: titulo || '' });
    return true;
  } catch (e) { return false; }
}
export async function removeFavorito(tipo, contenidoId) {
  try {
    await axios.delete(`${API}/canal-favoritos/${encodeURIComponent(tipo)}/${encodeURIComponent(contenidoId)}`);
    return true;
  } catch (e) { return false; }
}

// ── ACTIVIDAD (clases hechas >2/3, para la clase sugerida) ──
// Resumen del usuario logueado: última clase hecha + conteo por modalidad.
export async function getMiActividad() {
  if (!hasToken()) return null;
  try {
    const res = await axios.get(`${API}/canal-actividad/mia`, { timeout: 8000 });
    return res.data || null;
  } catch (e) { return null; }
}
// Registra un visionado de clase: completada=true (>2/3, cuenta para la rutina)
// o completada=false (la dejó a medias, para animarla a retomarla).
export async function registrarClaseHecha({ contenidoId, nivel, modalidad, titulo, progreso, completada = true }) {
  if (!hasToken()) return false;
  try {
    await axios.post(`${API}/canal-actividad/clase-hecha`, {
      contenido_id: contenidoId, nivel: nivel || '', modalidad: modalidad || '',
      titulo: titulo || '', progreso: typeof progreso === 'number' ? progreso : null,
      completada: !!completada,
    }, { timeout: 8000 });
    return true;
  } catch (e) { return false; }
}

// Desmarcar una clase dada por realizada (anula el último evento completada de ESA clase)
export async function desmarcarClaseHecha(contenidoId) {
  try {
    await axios.post(`${API}/canal-actividad/clase-hecha/desmarcar`, { contenido_id: contenidoId });
  } catch (e) { /* fire-and-forget */ }
}

// Registra una reproducción de cualquier contenido (contador de vistas).
export async function registrarReproduccion({ contenidoId, tipo, titulo }) {
  if (!hasToken() || !contenidoId) return false;
  try {
    await axios.post(`${API}/canal-actividad/reproduccion`, {
      contenido_id: contenidoId, tipo: tipo || '', titulo: titulo || '',
    }, { timeout: 8000 });
    return true;
  } catch (e) { return false; }
}

// PERMANENCIA por sección (Ann 2026-07-24: «quiero saber dónde va el usuario y qué
// tiempo pasa en cada sitio»). Se llama al SALIR de una sección (cambio de pantalla,
// cierre de pestaña o salida del canal). Silenciosa: si falla, la clienta no se entera.
// - beacon=true → sendBeacon con token por query (sobrevive al cierre de pestaña),
//   igual que registrarSalida.
// - beacon=false → axios normal (autenticado por la cabecera global) para los cambios
//   de sección con la app viva.
export function registrarPermanencia({ seccion, label, duracionSeg, beacon = false }) {
  if (!hasToken() || !seccion || !(duracionSeg >= 1)) return false;
  const body = { seccion, seccion_label: label || seccion, duracion_seg: Math.round(duracionSeg) };
  try {
    const url = `${API}/canal-actividad/permanencia`;
    if (beacon && navigator.sendBeacon) {
      const t = localStorage.getItem('token') || '';
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      return navigator.sendBeacon(url + `?token=${encodeURIComponent(t)}`, blob);
    }
    axios.post(url, body, { timeout: 5000 }).catch(() => {});
    return true;
  } catch (e) { return false; }
}

// ── VALORACIONES "Opiniones Google" (Ann 2026-07-24) ──
// ¿Le toca a esta clienta el popup de valoración? (1 sem usándolo, una vez, cooldown).
export async function deboPreguntarValoracion() {
  if (!hasToken()) return { preguntar: false };
  try {
    const res = await axios.get(`${API}/canal-valoraciones/debo-preguntar`, { timeout: 8000 });
    return res.data || { preguntar: false };
  } catch (e) { return { preguntar: false }; }
}
// Registra que se le ABRIÓ el popup (impresión), aunque no responda.
export function registrarValoracionMostrada(membresia) {
  if (!hasToken()) return false;
  try { axios.post(`${API}/canal-valoraciones/mostrado`, { membresia: membresia || '' }, { timeout: 6000 }).catch(() => {}); return true; }
  catch (e) { return false; }
}
// Guarda el voto (1-5 estrellas + comentario). Se llama al Enviar y al ir a Google.
export async function enviarVotoValoracion({ estrellas, comentario, fueAGoogle, membresia }) {
  if (!hasToken()) return false;
  try {
    await axios.post(`${API}/canal-valoraciones/votar`, {
      estrellas, comentario: comentario || '', fue_a_google: !!fueAGoogle, membresia: membresia || '',
    }, { timeout: 8000 });
    return true;
  } catch (e) { return false; }
}
// "Ahora no": pospone el popup (se reintenta tras el cooldown).
export function valoracionAhoraNo() {
  if (!hasToken()) return false;
  try { axios.post(`${API}/canal-valoraciones/ahora-no`, {}, { timeout: 6000 }).catch(() => {}); return true; }
  catch (e) { return false; }
}

// Refresco al abrir: pide un enlace FRESCO (TTL 2h) del vídeo de una clase justo
// al reproducir. Así el cliente legítimo nunca ve negro y el enlace dura poco.
export async function getClasePlayUrl(claseId) {
  if (!hasToken() || !claseId) return null;
  try {
    const res = await axios.get(`${API}/canal-clases/${encodeURIComponent(claseId)}/play-url/`, { timeout: 8000 });
    return res.data?.video_url || null;
  } catch (e) {
    // LÍMITE anti-repetición (HTTP 429, Ann 2026-07-16): el backend rechaza repetir
    // la misma clase dos veces el mismo día. Emitimos un evento con su mensaje para
    // que el blindaje del canal lo muestre (sin acoplarnos al reproductor).
    try {
      if (e?.response?.status === 429) {
        const msg = e.response.data?.detail || 'Hoy ya has hecho esta clase.';
        window.dispatchEvent(new CustomEvent('canal:limite-repeticion', { detail: msg }));
      }
    } catch (_) { /* noop */ }
    return null;
  }
}

// Mapa { contenido_id: vistas } para mostrar "X reproducciones" en el cliente.
export async function getConteoVistas() {
  const data = await safeGet('/canal-actividad/conteo-mapa', { auth: true });
  return (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
}

// Áreas dinámicas visibles para ESTE cliente (filtradas por su membresía en el backend).
export async function getAreasPublicas() {
  const data = await safeGet('/canal-areas/cliente', { auth: true });
  return Array.isArray(data) ? data : [];
}

// Estructura del área «Aplicaciones terapéuticas» para el canal (apps agrupadas por
// sub-enunciado). Devuelve null si no hay o falla → el componente cae a su home fijo.
export async function getCanalApps() {
  const data = await safeGet('/canal-apps/cliente', { auth: true });
  return (data && typeof data === 'object' && !Array.isArray(data)) ? data : null;
}

export async function getAreaContenido(key) {
  // El endpoint exige autenticación (control de acceso por membresía); sin sesión no se llama.
  const data = await safeGet(`/canal-area/${encodeURIComponent(key)}/fichas/wp/activas`, { auth: true });
  if (!Array.isArray(data)) return [];
  // MAPEO a la forma de item que consumen VideoCard y ScreenReproductor (nombre/
  // video/thumbnail/embedUrl), IGUAL que relajaciones/recomendaciones. Sin esto el
  // backend devolvía {titulo, video_url, thumbnail_url, embed_url} y el front leía
  // item.video/item.thumbnail (undefined): miniatura en verde y clic sin reproducir.
  return data.map((r, i) => ({
    id: r.id || `area-${key}-${i}`,
    tipo: 'area',
    contenidoId: r.id || `area-${key}-${i}`,
    nombre: r.titulo || 'Vídeo',
    zona: r.subtitulo || r.area_origen_label || '',
    duracion: r.duracion || null,
    thumbnail: r.thumbnail_url || null,
    video: r.video_url || null,
    embedUrl: r.embed_url || null,
    grad: 'g1',
    ..._camposFicha(r),
  }));
}

export async function getAreasConContenido() {
  const areas = await getAreasPublicas();
  const conItems = await Promise.all(areas.map(async a => ({ ...a, items: await getAreaContenido(a.key) })));
  return conItems.filter(a => a.items && a.items.length > 0); // solo áreas con algo que mostrar
}

// Carga todo en paralelo; cada pieza es null si no está disponible.
export async function loadCanalData() {
  const [semana, clases, relajaciones, extras, anuncio, botones, favoritos, actividad, charlas, novedades, vistas, areas] = await Promise.all([
    getSemanaActual(), getClasesBiblioteca(), getRelajaciones(),
    getRecomendaciones(), getAnuncio(), getBotones(), getFavoritos(), getMiActividad(), getCharlas(), getNovedades(), getConteoVistas(), getAreasConContenido(),
  ]);
  return { semana, clases, relajaciones, extras, anuncio, botones, favoritos, actividad, charlas, novedades, vistas, areas };
}

// ENTRADA y SALIDA del Canal (Ann 2026-07-20): para saber quién entra —aunque no
// abra nada— y cuánto se queda. Silenciosas: si fallan, la clienta ni se entera.
export async function registrarEntrada() {
  if (!hasToken()) return false;
  try { await axios.post(`${API}/canal-actividad/entrada`, {}, { timeout: 8000 }); return true; }
  catch (e) { return false; }
}

export function registrarSalida() {
  // Se dispara al cerrar la pestaña: `sendBeacon` es lo único que sobrevive a
  // que el navegador se cierre. Si no lo soporta, se intenta por la vía normal.
  if (!hasToken()) return false;
  try {
    const url = `${API}/canal-actividad/salida`;
    if (navigator.sendBeacon) {
      const t = localStorage.getItem('token') || '';
      const blob = new Blob([JSON.stringify({ t })], { type: 'application/json' });
      return navigator.sendBeacon(url + `?token=${encodeURIComponent(t)}`, blob);
    }
    axios.post(url, {}, { timeout: 3000 }).catch(() => {});
    return true;
  } catch (e) { return false; }
}

// [Ann 2026-10-23] Quien vuelve (o dejó el alta a medias) paga el Club desde el canal, por Stripe, sin pasar por la web.
export const crearCheckoutReingreso = () => axios.post(`${API}/canal/club/reingreso/checkout`);
